<template>
    <div>
        <div class="c-resizable">
            <div class="resizable-container" :style="{ height }" :class="containerClass">
                <div class="header">
                    <slot name="header" />
                </div>

                <div ref="contentRef" class="content">
                    <slot />
                </div>
            </div>
        </div>
        <div class="resize-handle" @mousedown="startResize">
            <font-awesome-icon icon="fa-solid fa-grip-dots" />
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue';

const props = defineProps({
    minHeight: { type: Number, default: 90 },
    maxHeight: { type: Number, default: 600 },
    containerClass: { type: String, default: '' }
});

const contentRef = ref(null);
const height = ref(props.minHeight + 'px');
const userResized = ref(false);

let startY = 0;
let startHeight = 0;
let mutationObserver;

const updateHeightFromContent = () => {
    if (!contentRef.value || userResized.value) return;

    const contentHeight = contentRef.value.scrollHeight;
    const headerHeight = contentRef.value.previousElementSibling?.offsetHeight || 0;

    const totalHeight = contentHeight + headerHeight;

    const clamped = Math.min(props.maxHeight, Math.max(props.minHeight, totalHeight));

    height.value = clamped + 'px';
};

onMounted(async () => {
    await nextTick();
    updateHeightFromContent();

    mutationObserver = new MutationObserver(updateHeightFromContent);
    mutationObserver.observe(contentRef.value, {
        childList: true,
        subtree: true,
        characterData: true
    });
});

const startResize = (e) => {
    e.preventDefault();
    userResized.value = true;

    startY = e.clientY;
    startHeight = parseInt(height.value, 10);

    document.addEventListener('mousemove', resize);
    document.addEventListener('mouseup', stopResize);
};

const resize = (e) => {
    const delta = e.clientY - startY;
    const next = startHeight + delta;

    height.value = Math.min(props.maxHeight, Math.max(props.minHeight, next)) + 'px';
};

const stopResize = () => {
    document.removeEventListener('mousemove', resize);
    document.removeEventListener('mouseup', stopResize);
};

onUnmounted(() => {
    mutationObserver?.disconnect();
    stopResize();
});
</script>

<style scoped>
.c-resizable {
    border: 1px solid #ddd;
    border-radius: 3px;
}
.resizable-container {
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.header {
    flex-shrink: 0;
}

.content {
    flex: 1;
    overflow: auto;
}

.resize-handle {
    cursor: row-resize;
    display: flex;
    justify-content: center;
    user-select: none;
}
</style>
